<template>
    <article class="tutorial-video" :class="{ 'tutorial-video--no-video': !hasVideo }" @click="openModal">
        <div class="tutorial-video__thumb">
            <img v-if="cover" :src="cover" :alt="title" loading="lazy" />
            <div v-if="hasVideo" class="tutorial-video__play">
                <Play />
            </div>
        </div>
        <p v-if="title" class="tutorial-video__title">{{ title }}</p>

        <TutorialVideoModal :visible="modalVisible" :src="videoUrl" :poster="cover" :title="title" :content="content"
            :chapters="chapters" @close="modalVisible = false" />
    </article>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { VideoChapter } from '@/stores/videoModal'
import { getStrWithRecord } from '.'
import Play from '@/assets/icons/play.svg'
import TutorialVideoModal from './TutorialVideoModal.vue'

const props = defineProps<{
    item: Record<string, unknown>
}>()

const modalVisible = ref(false)

const record = computed(() => props.item || {})

const title = computed(() => getStrWithRecord(record.value, 'title'))
const cover = computed(
    () => getStrWithRecord(record.value, 'img') || getStrWithRecord(record.value, 'image') || getStrWithRecord(record.value, 'cover') || getStrWithRecord(record.value, 'poster')
)
const videoUrl = computed(
    () => getStrWithRecord(record.value, 'video') || getStrWithRecord(record.value, 'videoUrl') || getStrWithRecord(record.value, 'url') || getStrWithRecord(record.value, 'src')
)
const content = computed(
    () => getStrWithRecord(record.value, 'richText') || getStrWithRecord(record.value, 'content') || getStrWithRecord(record.value, 'desc') || getStrWithRecord(record.value, 'description')
)
const chapters = computed<VideoChapter[]>(() => parseChapters(record.value.chapters || record.value.chapterList || record.value.chapter))
const hasVideo = computed(() => Boolean(videoUrl.value))

function parseChapters(raw: unknown): VideoChapter[] {
    if (!raw) return []
    if (Array.isArray(raw)) {
        return raw
            .map((item) => {
                const r = (item as Record<string, unknown>) || {}
                const name = String(r.name || r.title || '')
                const time = Number(r.time ?? r.second ?? 0)
                return { name, time: Number.isFinite(time) ? time : 0 }
            })
            .filter((ch) => ch.name)
    }
    if (typeof raw === 'string') {
        try {
            return parseChapters(JSON.parse(raw))
        } catch {
            return []
        }
    }
    return []
}

function openModal(): void {
    if (!hasVideo.value && !content.value && chapters.value.length === 0) return
    modalVisible.value = true
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

$orange: #ff6d00;

.tutorial-video {
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    &:hover {
        .tutorial-video__play {
            transform: translate(-50%, -50%) scale(1.1);
            box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
        }

        .tutorial-video__thumb img {
            transform: scale(1.05);
        }
    }

    &--no-video .tutorial-video__thumb::after {
        content: '';
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.15);
        pointer-events: none;
    }
}

.tutorial-video__thumb {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 10;
    border-radius: 0.5rem;
    overflow: hidden;
    background: #d9dbdf;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        transition: transform 0.4s ease;
    }
}

.tutorial-video__play {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    transition: transform 0.25s ease, box-shadow 0.25s ease;

    svg {
        width: 18px;
        height: 18px;
        color: $orange;
        margin-left: 2px;
    }

    @media (min-width: $breakpoint-md) {
        width: 52px;
        height: 52px;

        svg {
            width: 22px;
            height: 22px;
        }
    }
}

.tutorial-video__title {
    margin: 0;
    font-size: 0.8125rem;
    color: $color-text;
    line-height: 1.4;
    text-align: center;

    @media (min-width: $breakpoint-md) {
        font-size: $font-size-sm;
    }
}
</style>
